export const COUNTRY_EXTRA_CONFIG = {
    borderCrossing: {
        apiKey: "border-crossing",
        title: "Border Crossing",
        description:
            "Allow customers to travel into selected foreign countries. The configured fee is charged once per booking.",
    },
    ferryTickets: {
        apiKey: "ferry-tickets",
        title: "Ferry Tickets",
        description:
            "Offer ferry ticket arrangements for selected destination countries. The configured fee is charged once per booking.",
    },
    bikeAllowedOnFerry: {
        apiKey: "bike-allowed-on-ferry",
        title: "Bike Allowed on Ferry",
        description:
            "Specify the countries for which this motorcycle may be transported by ferry. The configured fee is charged once per booking.",
    },
};

export const COUNTRY_SERVICE_KEYS = Object.keys(COUNTRY_EXTRA_CONFIG);

const normalizeKey = (value) => String(value || "").trim().toLowerCase();

const findFormKeyByApiKey = (apiKey) =>
    COUNTRY_SERVICE_KEYS.find(
        (formKey) => COUNTRY_EXTRA_CONFIG[formKey].apiKey === normalizeKey(apiKey)
    );

export const createEmptyCountryServices = () =>
    COUNTRY_SERVICE_KEYS.reduce((result, formKey) => {
        result[formKey] = {
            enabled: false,
            description: "",
            countries: [],
        };
        return result;
    }, {});

export const buildCountryServicesInitialValues = (items = []) => {
    const result = createEmptyCountryServices();

    for (const item of Array.isArray(items) ? items : []) {
        const formKey = findFormKeyByApiKey(
            item?.key ?? item?.Extra?.key
        );

        if (!formKey) continue;

        result[formKey] = {
            enabled: true,
            description: item?.description ?? "",
            countries: (Array.isArray(item?.countries) ? item.countries : []).map(
                (country) => ({
                    id: country?.id || null,
                    destinationCountryId:
                        country?.destinationCountryId || country?.Country?.id || "",
                    price:
                        country?.priceCents == null
                            ? ""
                            : Number(country.priceCents) / 100,
                    description: country?.description ?? "",
                })
            ),
        };
    }

    return result;
};

export const buildCountryExtrasPayload = (
    services = {},
    internationalTravelAllowed = false
) => {
    if (!internationalTravelAllowed) return [];

    return COUNTRY_SERVICE_KEYS.filter(
        (formKey) => Boolean(services?.[formKey]?.enabled)
    ).map((formKey) => {
        const config = COUNTRY_EXTRA_CONFIG[formKey];
        const service = services?.[formKey] || {};

        return {
            key: config.apiKey,
            isRequired: false,
            description: service.description?.trim() || null,
            countries: (Array.isArray(service.countries)
                ? service.countries
                : []
            ).map((country) => ({
                destinationCountryId: country.destinationCountryId,
                priceCents: Math.round(Number(country.price) * 100),
                description: country.description?.trim() || null,
            })),
        };
    });
};

export const validateCountryServices = (values = {}, meta = {}) => {
    if (!values?.rentalPolicy?.countryRestriction) return "";

    const services = values?.countryServices || {};
    const countryIds = new Set((meta?.countries || []).map((item) => item.id));

    for (const formKey of COUNTRY_SERVICE_KEYS) {
        const config = COUNTRY_EXTRA_CONFIG[formKey];
        const service = services?.[formKey] || {};

        if (!service.enabled) continue;

        const countries = Array.isArray(service.countries)
            ? service.countries
            : [];

        if (!countries.length) {
            return `${config.title} is enabled. Add at least one country.`;
        }

        const seen = new Set();

        for (const country of countries) {
            const countryId = String(country?.destinationCountryId || "");

            if (!countryId) {
                return `${config.title} contains a country without an ID.`;
            }

            if (countryIds.size && !countryIds.has(countryId)) {
                return `${config.title} contains an unavailable country.`;
            }

            if (seen.has(countryId)) {
                return `${config.title} contains the same country more than once.`;
            }
            seen.add(countryId);

            if (
                country?.price === "" ||
                country?.price === null ||
                country?.price === undefined ||
                !Number.isFinite(Number(country.price)) ||
                Number(country.price) < 0
            ) {
                return `${config.title}: enter a valid one-time price for every country. Use 0 for a free service.`;
            }

            const cents = Number(country.price) * 100;
            if (!Number.isInteger(Math.round(cents))) {
                return `${config.title}: prices may contain at most two decimal places.`;
            }
        }
    }

    return "";
};
